import { useCallback, useMemo, useState } from 'react'
import {
  useReactTable,
  getPaginationRowModel,
  getCoreRowModel,
  getFilteredRowModel,
} from '@tanstack/react-table'
import * as XLSX from 'xlsx'
import { reportService } from '@/app/services/reportService'
import { format } from 'date-fns'
import { FiltersType } from '../components/FilterReports'
import { useReportSupports } from '@/app/hooks/useReportSupports'
import { TableReportSupportsColumnsDefinition } from './components/TableReportSupportsColumnsDefinition'
import {
  actionDic,
  AuditActionEnum,
  optionsDic,
  ReportSupports,
  ReportSupportsParams,
} from '@/app/services/reportService/reportSupports'

const AMOUNT_REPORTS_FETCH = Number(import.meta.env.VITE_AMOUNT_REPORTS_FETCH)

export const useReportSupportsController = () => {
  const [exportProgress, setExportProgress] = useState(0)
  const [isExporting, setIsExporting] = useState(false)
  const { data, isFetching, isLoading, setParams, params } = useReportSupports()

  const { columns } = TableReportSupportsColumnsDefinition()
  console.log(data)
  const table = useReactTable({
    data: data?.items || [],
    columns,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      pagination: {
        pageIndex: 0,
        pageSize: data?.pagination.limit || 100,
      },
    },
  })

  const fetchAllData = useCallback(async (): Promise<ReportSupports[]> => {
    if (!data) return []

    setIsExporting(true)
    setExportProgress(0)

    const batchSize = AMOUNT_REPORTS_FETCH
    const total = data.pagination.total
    let currentSkip = 0
    let allData: ReportSupports[] = []

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const batches: Promise<any>[] = []

    let completedBatches = 0

    while (total > currentSkip) {
      const exportParams: ReportSupportsParams = {
        ...params,
        limit: batchSize,
        skip: currentSkip,
      }

      const batchPromise = reportService
        .reportSupports(exportParams)
        .then((batchData) => {
          allData = [...allData, ...batchData.items]

          completedBatches += 1

          const progress = Number(
            Math.min(
              ((completedBatches * batchSize) / total) * 100,
              100,
            ).toFixed(0),
          )
          setExportProgress(progress)
        })
        .catch((error) => {
          console.error('Error fetching batch data:', error)
        })

      batches.push(batchPromise)

      currentSkip += batchSize
    }

    try {
      await Promise.all(batches)
    } catch (error) {
      console.error('Error awaiting batch resolutions:', error)
    }

    setIsExporting(false)
    return allData
  }, [data, params])

  const handleExportToExcel = useCallback(async () => {
    setIsExporting(true)
    const fetchedData = await fetchAllData()

    const formattedData = fetchedData.map((support) => ({
      date: support.createdAt
        ? format(support.createdAt, 'dd/MM/yyyy, HH:mm')
        : '',
      name: support.user?.name || '',
      action: actionDic[support.action],
      categories: support.props.options
        .map((category) => optionsDic[category])
        .filter(Boolean)
        .join(', '),
      search: support.props.values.join('; ') || '',
      target:
        support.action !== AuditActionEnum.SEARCH
          ? support.props.target.name
          : '',
    }))

    setIsExporting(false)

    if (formattedData.length > 0) {
      const workbook = XLSX.utils.book_new()
      const worksheet = XLSX.utils.json_to_sheet(formattedData)
      XLSX.utils.book_append_sheet(workbook, worksheet, 'ExportedReport')
      XLSX.utils.sheet_add_aoa(worksheet, [
        ['Data', 'Quem', 'Ação', 'Categoria', 'Conteúdo', 'Alvo'],
      ])
      XLSX.writeFile(workbook, 'supports-reports.xlsx')
    }
  }, [fetchAllData])

  const getPageIndex = () => {
    if (!data) return 0
    return Math.floor(data.pagination.skip / data.pagination.limit) + 1
  }

  const getPageCount = () => {
    if (!data) return 0
    return Math.ceil(data.pagination.total / data.pagination.limit)
  }

  const handleNextPage = () => {
    if (!data) return
    const newPage = data.pagination.skip / data.pagination.limit + 1
    if (getPageCount() === getPageIndex()) return
    const skipValue = newPage * data.pagination.limit
    setParams((p) => ({ ...p, skip: skipValue }))
  }

  const handlePreviousPage = () => {
    if (!data) return
    const newPage = data.pagination.skip / data.pagination.limit - 1
    if (newPage < 0) return
    const skipValue = newPage * data.pagination.limit
    setParams((p) => ({ ...p, skip: skipValue }))
  }

  const filters: FiltersType[] = useMemo(() => {
    const result: FiltersType[] = ['Date', 'Supports', 'SupportType']

    // if (!isSeller) {
    //   result.push('Sellers')
    //   if (!isLeader) {
    //     result.push('Leaders', 'Teams')
    //     if (!isManager) result.push('Managers')
    //   }
    // }
    return result
  }, [])
  return {
    table,
    filters,
    handleNextPage,
    handlePreviousPage,
    getPageIndex,
    getPageCount,
    isFetching,
    isLoading,
    setParams,
    isExporting,
    exportProgress,
    handleExportToExcel,
  }
}
